import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Button } from "antd";
import classnames from "classnames";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { GoogleMap, Circle, Rectangle, Polygon } from "../../../components/googleMap";
import { MELBOURNE_CBD } from "../../../components/googleMap/Model";
import { GeographicAreaShape } from "./api/Representation";
import GeographicAreaTable, {} from "./GeographicAreaTable";
import GeographicAreaEditForm from "./GeographicAreaEditForm";
import RequireTenantSelection from "../../../components/RequireTenantSelection";
import GeographicAreaShapePickerModal from "./GeographicAreaShapePickerModal";
import MapShapeSelectionOptions from "./MapShapeSelectionOptions";
import { MapAreaShapeTransformer } from "./GeographicAreaUIModel";
import GeographicAreaResource from "./api/GeographicAreaResource";
import { axiosErrorHandler } from "../../../../../swzutils";
import { selectedTenantSelector } from "../../../../../store/slice/auth";
import { useAppSelector } from "../../../../../store";
const GeographicAreaMapPage = () => {
    const [mapAreaShapes, setMapAreaShapes] = useState([]);
    const [editMapAreaShape, setEditMapAreaShape] = useState(undefined);
    const [mapCenter, setMapCenter] = useState(MELBOURNE_CBD);
    const [shapePickerModalOpen, setShapePickerModalOpen] = useState(false);
    const [networkRequestInProgress, setNetworkRequestInProgress] = useState(false);
    const [nextNewAreaKey, setNextNewAreaKey] = useState(1);
    const [selectedAreaId, setSelectedAreaId] = useState(undefined);
    const tableRef = useRef(null);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    useEffect(() => {
        setEditMapAreaShape(undefined);
    }, [selectedTenant?.id]);
    const refresh = () => {
        tableRef.current?.refresh();
    };
    const onNewShapeAdded = (shape, colourHex) => {
        setEditMapAreaShape(shape.build(`new-${nextNewAreaKey}`, "", colourHex, mapCenter));
        setShapePickerModalOpen(false);
        setNextNewAreaKey(nextNewAreaKey + 1);
    };
    const onRadiusChanged = (circle, radius) => {
        const updated = {
            ...circle,
            radius
        };
        setEditMapAreaShape(updated);
    };
    const onCenterChanged = (circle, center) => {
        const updated = {
            ...circle,
            center
        };
        setEditMapAreaShape(updated);
    };
    const onBoundsChanged = (rect, center, bounds) => {
        const updated = {
            ...rect,
            center,
            bounds
        };
        setEditMapAreaShape(updated);
    };
    const onPathChanged = (polygon, center, path) => {
        const updated = {
            ...polygon,
            center,
            path
        };
        setEditMapAreaShape(updated);
    };
    const onEditClicked = (areaResponse) => {
        const area = mapAreaShapes.find(s => s.id === areaResponse.id);
        if (area) {
            const copyOfShapeWithNewKey = {
                ...area,
                key: `edit-${area.id}`
            };
            setEditMapAreaShape(copyOfShapeWithNewKey); // Clone the area so it can be restored on cancel
            setSelectedAreaId(areaResponse.id);
        }
    };
    const onCloneClicked = (areaResponse) => {
        const existingShape = mapAreaShapes.find(s => s.id === areaResponse.id);
        if (existingShape) {
            const clonedShape = {
                ...existingShape,
                id: undefined,
                key: `new-${nextNewAreaKey}`
            };
            setEditMapAreaShape(clonedShape);
            setNextNewAreaKey(nextNewAreaKey + 1);
        }
    };
    const onSaveEditArea = (divisionId, title, priority, colourHex, disabledAt) => {
        if (editMapAreaShape) {
            const updateRequest = MapAreaShapeTransformer.toUpdateRequest(editMapAreaShape, selectedTenant.id, divisionId, title, priority, colourHex, editMapAreaShape.version, disabledAt);
            if (editMapAreaShape.id && editMapAreaShape.version) {
                updateArea(editMapAreaShape.id, updateRequest);
            }
            else {
                createNewArea(updateRequest);
            }
        }
    };
    const updateArea = (areaId, updateRequest) => {
        setNetworkRequestInProgress(true);
        GeographicAreaResource.update(areaId, updateRequest, response => {
            tableRef.current?.updateRecord(response);
            replaceArea(response);
            setEditMapAreaShape(undefined);
        }, axiosErrorHandler, () => setNetworkRequestInProgress(false));
    };
    const replaceArea = (area) => {
        const foundIndex = mapAreaShapes.findIndex(r => r.id === area.id);
        if (foundIndex >= 0) {
            const dataCopy = [...mapAreaShapes];
            dataCopy[foundIndex] = MapAreaShapeTransformer.fromResponse(area);
            setMapAreaShapes(dataCopy);
        }
    };
    const createNewArea = (updateRequest) => {
        setNetworkRequestInProgress(true);
        GeographicAreaResource.create(updateRequest, response => {
            tableRef.current?.addRecord(response);
            setEditMapAreaShape(undefined);
        }, axiosErrorHandler, () => setNetworkRequestInProgress(false));
    };
    const onColourChanged = (colourHex) => {
        const existingShapes = mapAreaShapes.filter(s => s.id !== editMapAreaShape?.id);
        if (existingShapes && editMapAreaShape) {
            const updatedShape = {
                ...editMapAreaShape,
                colourHex
            };
            setEditMapAreaShape(updatedShape);
        }
    };
    const onDataReceived = (data) => {
        setMapAreaShapes(data.map(MapAreaShapeTransformer.fromResponse));
    };
    const buildMapComponent = (area, editable) => {
        if (area.shape == GeographicAreaShape.CIRCLE) {
            const circle = area;
            return (_jsx(Circle, { radius: circle.radius, editable: editable, draggable: editable, strokeColor: circle.colourHex, strokeWeight: selectedAreaId == circle.id ? 4 : 1, lat: circle.center.lat, lng: circle.center.lng, onRadiusChanged: (radius) => onRadiusChanged(circle, radius), onCenterChanged: (center) => onCenterChanged(circle, center), onClick: () => setSelectedAreaId(circle.id) }, circle.key));
        }
        if (area.shape == GeographicAreaShape.RECTANGLE) {
            const rect = area;
            return (_jsx(Rectangle, { editable: editable, draggable: editable, strokeColor: rect.colourHex, strokeWeight: selectedAreaId == rect.id ? 4 : 1, bounds: rect.bounds, onBoundsChanged: (center, bounds) => onBoundsChanged(rect, center, bounds), onClick: () => setSelectedAreaId(rect.id) }, rect.key));
        }
        if (area.shape == GeographicAreaShape.POLYGON) {
            const polygon = area;
            return (_jsx(Polygon, { path: polygon.path, editable: editable, draggable: editable, strokeColor: polygon.colourHex, strokeWeight: selectedAreaId == polygon.id ? 4 : 1, onPathChanged: (center, path) => onPathChanged(polygon, center, path), onClick: () => setSelectedAreaId(polygon.id) }, polygon.key));
        }
        return null;
    };
    const buildMapComponents = () => {
        const shapeComponents = mapAreaShapes
            .filter(c => !editMapAreaShape || c.id !== editMapAreaShape.id)
            .map(c => buildMapComponent(c, false));
        if (editMapAreaShape) {
            shapeComponents.push(buildMapComponent(editMapAreaShape, true));
        }
        return shapeComponents;
    };
    return (_jsxs(_Fragment, { children: [_jsx(RequireTenantSelection, { children: _jsxs("div", { className: classnames('map-content', { 'map-search-hide': false }), children: [_jsxs("div", { className: "map-search", children: [_jsx("h2", { className: "article-title", children: "Delivery Areas" }), editMapAreaShape ?
                                    _jsx(GeographicAreaEditForm, { area: editMapAreaShape, onColourChanged: onColourChanged, onOk: onSaveEditArea, onCancel: () => setEditMapAreaShape(undefined) })
                                    :
                                        _jsxs(_Fragment, { children: [_jsxs("div", { className: "table-operations", children: [_jsx(Button, { onClick: () => setShapePickerModalOpen(true), icon: _jsx(PlusOutlined, {}), title: "Add Delivery Area", children: "Add Delivery Area" }), _jsx(Button, { className: "pull-right", onClick: refresh, title: "Refresh", icon: _jsx(ReloadOutlined, {}) })] }), _jsx(GeographicAreaTable, { loading: networkRequestInProgress, selectedRowId: selectedAreaId, onRowSelectionChanged: setSelectedAreaId, onDataReceived: onDataReceived, onEditClicked: onEditClicked, onCloneClicked: onCloneClicked })] })] }), _jsx("div", { className: "map-container", children: _jsx("div", { className: "map-body", children: _jsx(GoogleMap, { zoom: 10, initialCenter: mapCenter, onCenterChanged: setMapCenter, children: buildMapComponents() }) }) })] }) }), _jsx(GeographicAreaShapePickerModal, { open: shapePickerModalOpen, onOk: onNewShapeAdded, onCancel: () => setShapePickerModalOpen(false) })] }));
};
export default GeographicAreaMapPage;
